import { AfterViewInit, Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, Validators, ReactiveFormsModule } from '@angular/forms';
import { Api } from '../services/api';
import { NotificationService } from '../services/notification-service';
import { SessionService } from '../services/session-service';
import { GlobalService } from '../services/global-service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmDatePickerImports } from '@spartan-ng/helm/date-picker';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmAttachmentImports } from '@spartan-ng/helm/attachment';
import { HlmTabsImports } from '@spartan-ng/helm/tabs';
import { lucideCamera, lucideCloudUpload, lucideFilePenLine, lucideCheck, lucideCreditCard, lucideX , lucideVerified, lucideLockKeyhole, lucideTimer, lucideTriangleAlert, lucideArrowLeft, lucideArrowRight, lucideShieldCheck  } from '@ng-icons/lucide';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { CameraComponent } from '../components/camera-component/camera-component';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import SignaturePad from 'signature_pad';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { CommonModule } from '@angular/common';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { matArrowBackOutline } from '@ng-icons/material-symbols/outline';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { matArrowForwardOutline } from '@ng-icons/material-symbols/outline';
import { WelcomeStep } from "../components/steps/welcome-step/welcome-step";
import { PaymentFormStep } from "../components/steps/payment-form-step/payment-form-step";
import { PaymentStatusStep } from "../components/steps/payment-status-step/payment-status-step";
import { StatusCheckStep } from "../components/steps/status-check-step/status-check-step";
import { FooterComponent } from "../components/footer/footer.component";

interface Step {
  label: string;
  description: string;
}


@Component({
  selector: 'app-register-flow',
  standalone: true,
  imports: [NgIcon, CommonModule, RouterLink, FormsModule, HlmSeparatorImports, HlmBadgeImports, HlmItemImports, HlmSpinnerImports, HlmSkeletonImports, HlmButtonImports, HlmCardImports, HlmFieldImports, HlmInputImports, HlmInputGroupImports, HlmDatePickerImports, HlmSelectImports, HlmCheckboxImports, HlmAttachmentImports, HlmTabsImports, ReactiveFormsModule, WelcomeStep, PaymentFormStep, PaymentStatusStep, StatusCheckStep, FooterComponent],
  templateUrl: './register-flow.html',
  styleUrls: ['./register-flow.css'],
  providers: [provideIcons({matArrowForwardOutline, lucideCamera,lucideCheck, lucideX, lucideCloudUpload,lucideFilePenLine, lucideCreditCard, lucideVerified,matArrowBackOutline,lucideLockKeyhole,lucideTimer,lucideTriangleAlert, lucideArrowLeft, lucideArrowRight, lucideShieldCheck })]
  // host: {
  //   class: 'block',
  // },
})
export class RegisterFlowComponent implements OnInit, AfterViewInit {

  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private apiService = inject(Api);
  private globalService = inject(GlobalService);
  private readonly dialogService = inject(HlmDialogService);

  public maxDate = signal(new Date());
  public defaultFocusedDate = this.maxDate()

  public readonly maritalStatus = [
    { label: 'Single', value: 'single' },
    { label: 'Married', value: 'married' },
    { label: 'Divorced', value: 'divorced' },
    { label: 'Widowed', value: 'widowed' },
  ]
  public readonly itemToStringMaritalStatus = (value: string) => this.maritalStatus.find((item) => item.value === value)?.label || '';

  public readonly regionList = [
    { label: 'Arewa Community', value: '1' },
    { label: 'Igbo Community', value: '2' },
    { label: 'South South Community', value: '3' },
    { label: 'Yoruba Community', value: '4' },
  ]
  public readonly itemToStringRegionList = (value: string) => this.regionList.find((item) => item.value === value)?.label || '';

  public readonly networkList = [
    { label: 'MTN', value: 'MTN' },
    { label: 'Telecel', value: 'VOD' },
    { label: 'AirTel', value: 'AIR' },
  ]
  public readonly itemToStringNetworkList = (value: string) => this.networkList.find((item) => item.value === value)?.label || '';


  formSubData = signal<any>(null);

  // ancForm: FormGroup;
  submitted = signal(false);
  serialNumber = signal<string | undefined>(undefined);
  registrationNumber = signal<string | undefined>(undefined);

  isLoading = signal(false);
  isPaymentLoading = signal(false);

  passportPreview = signal<string | null>(null);
  passportSize = signal(0);
  passportError = signal<string | null>(null);
  @ViewChild('passportInput') passportInput!: ElementRef;


  signatureFilePreview = signal<string | null>(null);
  signatureFileSize = signal(0);
  signatureFileError = signal<string | null>(null);

  refereeSignaturePreview = signal<string | null>(null);
  refereeSignatureSize = signal(0);
  refereeSignatureError = signal<string | null>(null);

  readonly MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

  @ViewChild('sigPadCanvas') sigPadCanvas!: ElementRef<HTMLCanvasElement>;
  signaturePad!: SignaturePad;
  signaturePadPreview = signal<string | null>(null);
  @ViewChild('signatureInput') signatureInput!: ElementRef;


  // Referee and Chairman signature pads
  @ViewChild('refereeSigCanvas') refereeSigCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('refereeInput') refereeInput!: ElementRef;
  refereePad!: SignaturePad;
  refereePadPreview = signal<string | null>(null);
  @ViewChild('video', { static: true }) videoElement!: ElementRef<HTMLVideoElement>;
  photo = signal<string | null>(null);


  // @ViewChild('dateModal') dateModal!: IonModal;

  segmentValue = signal<any>('first')
  segmentValue_2 = signal('first');


  sessionData = signal<any>(null);

  // Helper method to check if a field is invalid
  isFieldInvalid(fieldName: string): boolean {
    const field = this.ancForm.get(fieldName);
    return (field?.invalid && (field.dirty || field.touched || this.submitted())) ?? false;
  }

  // Get error message for a field
  getErrorMessage(fieldName: string): string {
    const control = this.ancForm.get(fieldName);

    if (!control || !control.errors) return '';

    if (control.errors['required'] || control.errors['requiredTrue']) {
      // Handles both standard required and requiredTrue (for checkboxes)
      if (fieldName === 'declaration' && control.errors['requiredTrue']) {
        return 'You must confirm the declaration to proceed';
      }
      return 'This field is required';
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address';
    }
    if (control.errors['pattern']) {
      if (fieldName.toLowerCase().includes('phone')) {
        return 'Please enter a valid phone number (10 digits starting with 0)';
      }
      return 'Invalid format';
    }

    return 'Invalid input';
  }

  getErrorMessagePayment(fieldName: string): string {
    const control = this.paymentForm.get(fieldName);

    if (!control || !control.errors) return '';

    if (control.errors['required'] || control.errors['requiredTrue']) {
      // Handles both standard required and requiredTrue (for checkboxes)
      if (fieldName === 'declaration' && control.errors['requiredTrue']) {
        return 'You must confirm the declaration to proceed';
      }
      return 'This field is required';
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address';
    }
    if (control.errors['pattern']) {
      if (fieldName.toLowerCase().includes('phone')) {
        return 'Please enter a valid phone number (10 digits starting with 0)';
      }
      return 'Invalid format';
    }

    return 'Invalid input';
  }



  ancForm:FormGroup = this.fb.group({
      fullName: ['', [Validators.required]],
      name_of_kvtl: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required,Validators.pattern('^0\\d{9}$')]],
      dateOfBirth: ['', [Validators.required]],
      occupation: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      maritalStatus: ['', [Validators.required]],
      region: ['', [Validators.required]],
      ghanaAddress: ['', [Validators.required]],
      nigeriaAddress: ['', [Validators.required]],
      refereesNames: ['', [Validators.required]],
      refereesPhoneNumber: ['', [Validators.required, Validators.pattern('^0\\d{9}$')]],
      refereesAddress: ['', [Validators.required]],

      passport: [null as File | null, [Validators.required]],
      signature: [null as File | null, [Validators.required]],

      benNextOfKin: ['', [Validators.required]],
      benRelationship: ['', [Validators.required]],
      benPhoneNumber: ['', [Validators.required, Validators.pattern('^0\\d{9}$')]],
      declaration: [false, [Validators.requiredTrue]],

      localGovernment: ['',[Validators.required]],
      stateOrigin: ['',[Validators.required]],
      // refereesSignature: [null as File | null,],

    });

  paymentForm:FormGroup = this.fb.group({
    serial_number: ['',[Validators.required]],
    payment_number: ['', [Validators.required, Validators.pattern('^0\\d{9}$')]],
    network: ['',[Validators.required]]
  })




  currentStep = signal(0);

  steps: Step[] = [
    { label: 'Welcome', description: 'Three simple stages to complete your membership registration.' },
    { label: 'Application Form', description: 'Provide your particulars, then upload your passport photograph and signature.' },
    { label: 'Payment', description: 'Settle the application fee securely via Mobile Money.' },
    { label: 'Confirmation', description: 'We verify your transaction with the network provider.' },
    { label: 'Status Check', description: 'Track the progress of your submitted application.' },
  ];


  paymentState = signal<'pending'|'success'|'failed'|null>(null)
  isCheckingPayment = signal<boolean>(false)
  private paymentStatusIntervalRef?: ReturnType<typeof setInterval>
  private paymentStatusTimeoutRef?: ReturnType<typeof setTimeout>

  // Application status signals for step 4
  applicationLoading = signal<boolean>(false)
  applicationStatus = signal<'approved'|'pending'|'cancelled'|null>(null)
  applicationData = signal<any>(null)
  applicationError = signal<string | null>(null)
  showSubmittedApplication = signal<boolean>(false)


  initSignaturePad() {
    if (this.sigPadCanvas) {
      const canvas = this.sigPadCanvas.nativeElement;
      if (this.signaturePad) {
        this.signaturePad.off();
      }
      this.signaturePad = new SignaturePad(canvas, {
        minWidth: 1,
        maxWidth: 2,
        penColor: 'rgb(0, 0, 0)'
      });
    }
  }

  onNext(event?:any): void {
    // toast('Event has been created.');
    if (this.currentStep() < this.steps.length - 1) {
      this.currentStep.update(v => v + 1);
      if (this.currentStep() === 1) {
        requestAnimationFrame(() => this.initSignaturePad());
      }
    }
  }

  onBack(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update(v => v - 1);
      if (this.currentStep() === 1) {
        requestAnimationFrame(() => this.initSignaturePad());
      }
    }
  }


  constructor(){

  }




  // // // stating functions

  ngOnInit(): void {
    // reset on init
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => this.initSignaturePad());
    this.formSubData.set(this.sessionService.getData())
    // console.log(this.formSubData()?.user?.serial_number)
  }



  async openModal(){
    const modal = this.globalService.showModal(CameraComponent,{},{showCloseButton:true,disableClose:true})
    modal.closed$.subscribe(async (data:any) => {
      console.log('asda',data)
        if (data?.data) {
           await this.processPhotoFromCamera(data.data);
        }
      });

  }

  onDateChange(event: any) {
    const a = event.detail.value;
    this.ancForm.patchValue({ dateOfBirth: this.formatDate(new Date(a)) });
  }

  formatDate(dateValue: string | Date): string {
    const date = new Date(dateValue);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private async processPhotoFromCamera(photoData: string) {
    this.passportPreview.set(photoData);

    const file = await this.base64ToFile(photoData, `${this.serialNumber()}-passport.png`, 'image/png');
    this.passportSize.set(file.size);
    this.passportError.set(null);
    this.ancForm.patchValue({ passport: file });
    console.log('Photo captured from camera and updated form',this.passportSize());
  }

  private base64ToFile(base64: string, filename: string, mimeType: string): Promise<File> {
    return new Promise((resolve) => {
      const arr = base64.split(',');
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], filename, { type: mimeType });
      resolve(file);
    });
  }

  async onSubmit() {
    console.log(this.ancForm.value);
    // return;
    this.submitted.set(true);

    if (this.ancForm.invalid) {
      Object.keys(this.ancForm.controls).forEach(key => {
        const control = this.ancForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    const formData = this.buildFormData();
    console.log(this.ancForm.value);

    this.isLoading.set(true);

    this.sendFormData(formData).finally(()=> this.isLoading.set(false))
    // this.apiService.postData(formData, environment.signUp).subscribe({
    //   next: (response: HttpResponse<any>) => {
    //     this.isLoading.set(false);
    //     if (response.status === 200 || response.status === 201) {
    //       console.log('response body', response.body);
    //       this.notificationService.notify('Membership form submitted successfully','success',{duration:3000,position:'top-right'} );
    //           this.sessionService.saveData(response.body);
    //       this.router.navigate(['/membership-form'], { replaceUrl: true });
    //     }
    //    },
    //   error: (err: HttpErrorResponse) => {
    //     console.log('error error', err.error);
    //     this.isLoading.set(false);
    //     const errorMessage = (err.error && err.error.message) ? err.error.message : 'Something went wrong';
    //     this.notificationService.notify(errorMessage,'error',{duration:3000} );
    //   }
    // });


  }

  sendFormData(formData:any){

    return new Promise<void>((resolve, reject) => {
      this.apiService.postData(formData, environment.signUp).subscribe({
      next: (response: HttpResponse<any>) => {
        this.isLoading.set(false);
        if (response.status === 200 || response.status === 201) {
          console.log('response body', response.body);
          this.notificationService.notify('Membership form submitted successfully','success',{duration:3000,position:'top-right'} );
          const trimData = {user:{serial_number:response.body?.user?.serial_number,mobile_number:response.body?.user?.registration_number,amount: response.body?.user?.amount}}
          this.sessionService.formSubData.set(trimData)

              // this.formSubData.set(trimData)
              this.onNext()
              // this.paymentForm.patchValue({serial_number:this.formSubData()?.user?.serial_number})
          // this.router.navigate(['/membership-form'], { replaceUrl: true });
        }
       },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        const errorMessage = (err.error && err.error.message) ? err.error.message : 'Something went wrong';
        this.notificationService.notify(errorMessage,'error',{duration:3000} );
      }
    });
    })
  }

  // async onPaymentSubmit(){
  //   this.paymentForm.patchValue({serial_number: this.formSubData()?.user?.serial_number})
  //   console.log(this.paymentForm.value)
  //   if (this.paymentForm.invalid) {
  //     Object.keys(this.paymentForm.controls).forEach(key => {
  //       const control = this.paymentForm.get(key);
  //       control?.markAsTouched();
  //     });
  //     return;
  //   }

  //   this.isPaymentLoading.set(true)
  //   this.sendPaymentData(this.paymentForm.value).finally(()=> this.isPaymentLoading.set(false))
  // }

  // async sendPaymentData(data:any){
  //   console.log(data)
  //   // return
  //   return new Promise<boolean>((resolve, reject) => {
  //     this.apiService.postData(data, environment.submitPayment).subscribe({
  //     next: (response: HttpResponse<any>) => {
  //       // this.isLoading.set(false);
  //       if (response.status === 200 || response.status === 201) {
  //         console.log('response body', response.body);

  //         if(response.body?.status === 'success'){
  //           this.onNext()
  //           this.startPaymentStatusCheck()
  //           this.notificationService.notify(response.body?.message,'success',{duration:3000,position:'top-right'} );
  //         }else{
  //           this.notificationService.notify(response.body?.message,'warning',{duration:3000,position:'bottom-right'} );
  //         }
  //         resolve(true)

  //       }
  //      },
  //     error: (err: HttpErrorResponse) => {
  //       console.log('error error', err.error);
  //       resolve(false)
  //       const errorMessage = (err.error && err.error.message) ? err.error.message : 'Something went wrong';
  //       this.notificationService.notify(errorMessage,'error',{duration:3000} );
  //     }
  //   });
  //   })
  // }

  // retryPaymentStatusCheck(){
  //   this.startPaymentStatusCheck()
  // }

  // private startPaymentStatusCheck(){
  //   this.stopPaymentStatusCheck()
  //   this.checkPaymentStatusOnce()

  //   this.paymentState.set('pending')
  //   this.isCheckingPayment.set(true)

  //   this.paymentStatusIntervalRef = setInterval( ()=>{
  //     this.checkPaymentStatusOnce()
  //   },10000 )

  //   this.paymentStatusTimeoutRef = setTimeout( ()=>{
  //     this.isCheckingPayment.set(false)
  //     this.stopPaymentStatusCheck(false)

  //   },60000)
  // }

  // private stopPaymentStatusCheck( resetState:boolean = true){

  //   if(this.paymentStatusIntervalRef){
  //     clearInterval(this.paymentStatusIntervalRef)
  //     this.paymentStatusIntervalRef = undefined
  //   }

  //   if(this.paymentStatusTimeoutRef){
  //     clearTimeout(this.paymentStatusTimeoutRef)
  //     this.paymentStatusTimeoutRef = undefined
  //   }

  //   if(resetState){
  //     // this.paymentState.set('pending')
  //     this.isCheckingPayment.set(false)
  //   }
  // }

  // checkPaymentStatusOnce(){
  //   // api call here to get the response and stop
  //   this.checkPaymentStatus().then( (isSuccess)=>{
  //     console.log(this.paymentState())
  //     if(!isSuccess)return;

  //     this.stopPaymentStatusCheck()
  //   } )
  // }

  // async checkPaymentStatus(){

  //   const data = {serial_number: this.formSubData()?.user?.serial_number}
  //   return new Promise<boolean>((resolve, reject) => {
  //     this.apiService.postData(data, environment.checkPaymentStatus).subscribe({
  //     next: (response: HttpResponse<any>) => {
  //       // this.isLoading.set(false);
  //       if (response.status === 200 || response.status === 201) {
  //         console.log('response body', response.body);

  //         if(response.body?.status === 'success'){
  //           switch (response.body?.payment_status) {
  //             case "successful":
  //               this.paymentState.set('success')
  //               resolve(true)
  //               break;
  //           case "failed":
  //             this.paymentState.set('failed')
  //             resolve(true)
  //             break;

  //             default:
  //               this.paymentState.set('pending')
  //               resolve(false)
  //               break;
  //           }
  //           // this.onNext()
  //           // this.notificationService.notify(response.body?.message,'success',{duration:3000,position:'top-right'} );
  //         }else{
  //           // this.notificationService.notify(response.body?.message,'warning',{duration:3000,position:'bottom-right'} );
  //           resolve(false)
  //         }


  //       }
  //      },
  //     error: (err: HttpErrorResponse) => {
  //       console.log('error error', err.error);
  //       resolve(false)
  //       const errorMessage = (err.error && err.error.message) ? err.error.message : 'Something went wrong';
  //       // this.notificationService.notify(errorMessage,'error',{duration:3000} );
  //     }
  //   });
  //   })
  // }

  // Build FormData with requested keys (without the leading colon)
  private buildFormData(): FormData {
    const v = this.ancForm.value as any;
    const fd = new FormData();

    // Text fields
    fd.append('full_name', v.fullName );
    fd.append('date_of_birth', v.dateOfBirth );
    fd.append('mobile_number', v.phoneNumber );
    fd.append('occupation', v.occupation );
    fd.append('email', v.email );
    fd.append('name_of_kvtl', v.name_of_kvtl );
    fd.append('marital_status', v.maritalStatus );
    fd.append('region', v.region );
    fd.append('ghana_address', v.ghanaAddress );
    fd.append('nigeria_address', v.nigeriaAddress );
    fd.append('ghana_referee', v.refereesNames );
    fd.append('phone_referee', v.refereesPhoneNumber );
    fd.append('address_referee', v.refereesAddress );

    // Next of kin (using beneficiary fields by default; falls back to top-level nextOfKin if needed)
    fd.append('next_of_kin', v.benNextOfKin );
    fd.append('kin_relationship', v.benRelationship );
    fd.append('kin_contact_number', v.benPhoneNumber );

    fd.append('local_government', v.localGovernment );
    fd.append('state_origin', v.stateOrigin );

    // Backend-controlled or optional fields (left blank for now so you can set as needed)
    // fd.append('status', '');
    // fd.append('final_response', '');

    // Registration/region mapping
    // fd.append('serial_number', this.serialNumber() != null ? String(this.serialNumber()) : '');
    fd.append('registration_number', v.phoneNumber);


    // Files
    const passport: string | File  = v.passport || null;
    if (passport && passport instanceof File) {
      fd.append('passport_photo', passport, passport.name);
    }else{
      fd.append('passport_photo', passport);
    }
    const signature: string | File  = v.signature || null;

    if (signature && signature instanceof File) {
      fd.append('signature_photo', signature, signature.name);
    }else{
      fd.append('signature_photo', signature);
    }

    return fd;
  }


  clearPassport() {
    this.passportPreview.set(null);
    this.passportSize.set(0);
    this.ancForm.patchValue({ passport: null });
    this.passportInput.nativeElement.value = '';
  }

  onSignatureFileChange(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.signatureFileSize.set(file.size);
      this.signatureFileError.set(null);

      if (file.size > this.MAX_FILE_SIZE) {
        this.signatureFileError.set('File size exceeds 2MB limit');
        this.clearSignatureFile();
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.signatureFilePreview.set(e.target.result);

        const mime = file.name.split('.').pop();
        const newFileName = `${this.serialNumber()}-signature.${mime}`;
        const renamedFile = new File([file], newFileName, { type: file.type });

        console.log('Signature file:', renamedFile.name);
        this.ancForm.patchValue({ signature: renamedFile });
      };
      reader.readAsDataURL(file);
    }
  }

  clearSignatureFile() {
    this.signatureFilePreview.set(null);
    this.signatureFileSize.set(0);
    this.ancForm.patchValue({ signature: null });
    this.signatureInput.nativeElement.value = '';
  }

  saveSignature() {
    if (this.signaturePad && !this.signaturePad.isEmpty()) {
      const dataUrl = this.signaturePad.toDataURL();
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      const file = new File([u8arr], `${this.serialNumber()}-signature.png`, { type: mime });

      this.signaturePadPreview.set(dataUrl);
      this.ancForm.patchValue({ signature: file });
      if (this.signatureInput) this.signatureInput.nativeElement.value = '';
    }
  }

  clearSignaturePad() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.signaturePadPreview.set(null);
      this.ancForm.patchValue({ signature: null });
      if (this.signatureInput) this.signatureInput.nativeElement.value = '';
    }
  }

  clearSignaturePadPreview() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.signaturePadPreview.set(null);
      this.ancForm.patchValue({ signature: null });
      if (this.signatureInput) this.signatureInput.nativeElement.value = '';
    }
  }

  // private fileToDataURL(file: File): Promise<string> {
  //   return new Promise((resolve, reject) => {
  //     try {
  //       const reader = new FileReader();
  //       reader.onload = (e: any) => resolve(e.target.result as string);
  //       reader.onerror = (err) => reject(err);
  //       reader.readAsDataURL(file);
  //     } catch (e) {
  //       reject(e);
  //     }
  //   });
  // }

  // checkApplicationStatus(){
  //   this.checkingApplicationStatus()
  // }

  // toggleSubmittedApplication(){
  //   this.showSubmittedApplication.update(v => !v)
  // }

  // checkingApplicationStatus(){
  //   const data = {serial_number: this.sessionService.formSubData()?.user?.serial_number, mobile_number: this.sessionService.formSubData()?.user?.mobile_number}
  //   this.applicationLoading.set(true)
  //   this.applicationError.set(null)
  //   return new Promise<boolean>((resolve, reject) => {
  //     this.apiService.postData(data, environment.checkApplicationStatus).subscribe({
  //     next: (response: HttpResponse<any>) => {
  //       this.applicationLoading.set(false)
  //       if (response.status === 200 || response.status === 201) {
  //         console.log('check application response', response.body);

  //         if(response.body?.status === 'success'){
  //           // Store the full response for UI
  //           this.applicationData.set(response.body)
  //           const appStatus = response.body?.application_status ?? null
  //           if(appStatus === 'approved'){
  //             this.applicationStatus.set('approved')
  //           } else if(appStatus === 'pending'){
  //             this.applicationStatus.set('pending')
  //           } else if(appStatus === 'cancelled'){
  //             this.applicationStatus.set('cancelled')
  //           } else {
  //             this.applicationStatus.set(null)
  //           }

  //           resolve(true)
  //         } else {
  //           // API returned an error payload (not found / mismatch)
  //           const msg = response.body?.message || 'Application not found';
  //           this.applicationError.set(msg)
  //           this.applicationData.set(null)
  //           this.applicationStatus.set(null)
  //           resolve(false)
  //         }
  //       } else {
  //         this.applicationLoading.set(false)
  //         this.applicationError.set('Unexpected response from server')
  //         this.applicationData.set(null)
  //         this.applicationStatus.set(null)
  //         resolve(false)
  //       }
  //      },
  //     error: (err: HttpErrorResponse) => {
  //       console.log('error checking application', err.error);
  //       this.applicationLoading.set(false)
  //       const errorMessage = (err.error && err.error.message) ? err.error.message : 'Something went wrong';
  //       this.applicationError.set(errorMessage)
  //       this.applicationData.set(null)
  //       this.applicationStatus.set(null)
  //       resolve(false)
  //     }
  //   });
  //   })
  // }



  onPaymentContinue(event:any){
    if(!event)return;

    this.onNext()
  }

  onPaymentStatusContinue(event:any){
    if(!event){
      this.onBack()
      return
    }
    this.router.navigate(['/login'])

    this.onNext()
  }
}
